import { SlidersHorizontal, X } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { Drawer } from '@/components/ui/Drawer'
import { Input } from '@/components/ui/Input'
import { toggleClass } from '@/components/ui/toggle'
import { cn } from '@/lib/cn'
import { fmtInt } from '@/lib/format'

/** An active filter, shown as a removable chip («Статус: Готово ✕»). */
export interface ActiveFilter {
  id: string
  label: string
  onRemove: () => void
}

/**
 * One row of filters above a list (WP-14, design §6.12): no card around it, every control
 * 40 px (`Input`/`Select size="sm"`, `Segmented`, `DateRange`), search first and widest.
 * Secondary filters wait behind «Ещё фильтры (2)». Active filters repeat as removable chips
 * with «Сбросить все», so a filtered list never hides why it is short.
 *
 * Below `md` (WP-15) the row collapses to the search and «Фильтры (N)», which opens every
 * control in a left drawer that ends with «Показать 1 204»: the first rows of the list stay on
 * the first screen instead of 600 px of stacked selects. There the visible label is for screen
 * readers only and the field shares its row, so the placeholder is short («Поиск», §6.3: it
 * fits its field) and the full hint («id, Telegram ID, @username») shows under the field while
 * it has focus — and is its description for a screen reader.
 *
 * Controls are the page's own (controlled by its state) and render in one place at a time: the
 * row on a wide screen, the drawer on a phone.
 */
export function FilterBar({
  search,
  children,
  more,
  moreActive = 0,
  active = [],
  onReset,
  total,
  defaultOpen,
  className,
}: {
  search?: {
    value: string
    onChange: (v: string) => void
    /** what can be typed («id, Telegram ID, @username»): the placeholder of the wide row */
    placeholder: string
    label?: string
    /** the placeholder on a phone (default «Поиск»); the full one shows under the field on focus */
    short?: string
  }
  /** the main controls, always in the row */
  children?: ReactNode
  /** secondary controls, behind «Ещё фильтры» */
  more?: ReactNode
  /** how many of the secondary filters are set (the count on «Ещё фильтры») */
  moreActive?: number
  active?: ActiveFilter[]
  onReset?: () => void
  /** rows matching now, for «Показать 1 204» in the phone drawer */
  total?: number
  /** start with «Ещё фильтры» or the phone drawer open (stories) */
  defaultOpen?: { more?: boolean; sheet?: boolean }
  className?: string
}) {
  const [moreOpen, setMoreOpen] = useState(moreActive > 0 || defaultOpen?.more === true)
  const [sheet, setSheet] = useState(defaultOpen?.sheet === true)
  const searchId = useId()
  const sheetId = useId()
  const controls = children !== undefined || more !== undefined
  const count = active.length

  // Rendered twice (the wide row and the phone row, one hidden by CSS): its own id in each.
  const searchField = (where: 'row' | 'phone') =>
    search && (
    <div className="group min-w-0 flex-1 md:min-w-[280px] md:max-w-xl">
      <label
        htmlFor={`${searchId}-${where}`}
        className={where === 'phone' ? 'sr-only' : 'mb-1.5 block text-caption text-ink-2'}
      >
        {search.label ?? 'Поиск'}
      </label>
      <Input
        id={`${searchId}-${where}`}
        size="sm"
        type="search"
        placeholder={where === 'phone' ? (search.short ?? 'Поиск') : search.placeholder}
        aria-describedby={where === 'phone' ? `${searchId}-hint` : undefined}
        value={search.value}
        onChange={(e) => search.onChange(e.target.value)}
      />
      {where === 'phone' && (
        <p id={`${searchId}-hint`} className="mt-1 hidden text-caption text-ink-3 group-focus-within:block">
          {search.placeholder}
        </p>
      )}
    </div>
  )

  return (
    <div className={cn('mb-4 flex flex-col gap-3', className)}>
      {/* md and up: one row */}
      <div className="hidden flex-wrap items-end gap-3 md:flex print:hidden">
        {searchField('row')}
        {children}
        {more !== undefined && (
          <button
            type="button"
            aria-expanded={moreOpen}
            className={toggleClass(moreOpen, 'md')}
            onClick={() => setMoreOpen((o) => !o)}
          >
            <SlidersHorizontal aria-hidden size={16} strokeWidth={1.75} />
            Ещё фильтры
            {moreActive > 0 && <span className="tnum font-normal">({fmtInt(moreActive)})</span>}
          </button>
        )}
        {moreOpen && more}
      </div>

      {/* below md: the search and «Фильтры (N)» */}
      <div className="flex items-start gap-2 md:hidden print:hidden">
        {searchField('phone')}
        {controls && (
          <Button
            variant="secondary"
            className="h-10 shrink-0"
            aria-expanded={sheet}
            aria-controls={sheetId}
            onClick={() => setSheet(true)}
          >
            <SlidersHorizontal aria-hidden size={16} strokeWidth={1.75} />
            Фильтры
            {count > 0 && <span className="tnum font-normal">({fmtInt(count)})</span>}
          </Button>
        )}
      </div>

      {count > 0 && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Активные фильтры" role="group">
          {active.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={f.onRemove}
              aria-label={`Убрать фильтр: ${f.label}`}
              className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border border-cyan bg-cyan-soft px-3 text-caption text-ink-1 [@media(pointer:coarse)]:min-h-11"
            >
              <span className="truncate">{f.label}</span>
              <X aria-hidden size={14} strokeWidth={1.75} className="shrink-0 text-ink-2 print:hidden" />
            </button>
          ))}
          {onReset && count > 1 && (
            <Button variant="ghost" size="sm" onClick={onReset}>
              Сбросить все
            </Button>
          )}
        </div>
      )}

      {sheet && (
        <Drawer
          id={sheetId}
          open
          side="left"
          title="Фильтры"
          onClose={() => setSheet(false)}
          footer={
            <div className="flex flex-col gap-2">
              <Button variant="primary" size="lg" full onClick={() => setSheet(false)}>
                {total === undefined ? 'Показать' : `Показать ${fmtInt(total)}`}
              </Button>
              {onReset && count > 0 && (
                <Button variant="ghost" full onClick={onReset}>
                  Сбросить все
                </Button>
              )}
            </div>
          }
        >
          <div className="flex flex-col gap-5 [&>*]:w-full">
            {children}
            {more}
          </div>
        </Drawer>
      )}
    </div>
  )
}
